import React from "react";
import { Box, Checkbox, IconButton, Tooltip, useTheme } from "@mui/material";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { AlertItem } from "../../../../../models/Alerts/Alert";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import {
  SectionIcon,
  formatAlertDateTime,
  formatAlertRelative,
  getAlertColors,
  getResolvedTone,
  getSeverityTone,
} from "./alertUi";
import { AlertPill } from "./AlertPills";

interface AlertRowProps {
  alert: AlertItem;
  compact?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onOpen: () => void;
  onOpenDetail: () => void;
  onMarkRead?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
}

const AlertRow: React.FC<AlertRowProps> = ({
  alert,
  compact = false,
  selectable = false,
  selected = false,
  onToggleSelect,
  onOpen,
  onOpenDetail,
  onMarkRead,
  onArchive,
  onUnarchive,
}) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const tone = getSeverityTone(theme, alert.severity);
  const resolvedTone = getResolvedTone(theme);
  const isUnread = alert.me.read_at == null;
  const isArchived = alert.me.archived_at != null;
  const isResolved = alert.status === "RESOLVED";

  const stop =
    (handler?: () => void) => (event: React.MouseEvent | React.ChangeEvent) => {
      event.stopPropagation();
      handler?.();
    };

  const actionSx = {
    width: 30,
    height: 30,
    borderRadius: "8px",
    color: colors.subtitle,
    bgcolor: colors.surface,
    border: `1px solid ${colors.border}`,
    "&:hover": { color: colors.accent, borderColor: colors.accent },
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter") onOpen();
      }}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: compact ? 2 : 2.5,
        py: compact ? 1.5 : 1.75,
        cursor: "pointer",
        bgcolor: selected
          ? colors.hover
          : isUnread
            ? colors.unreadBg
            : "transparent",
        borderBottom: `1px solid ${colors.hairline}`,
        transition: "background-color 0.15s ease",
        outline: "none",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 10,
          bottom: 10,
          width: 3,
          borderRadius: "0 3px 3px 0",
          bgcolor: isResolved ? "transparent" : tone.fg,
          opacity: isUnread ? 1 : 0.35,
        },
        "&:hover, &:focus-visible": { bgcolor: colors.hover },
        "&:hover .alert-row-actions, &:focus-within .alert-row-actions": {
          opacity: 1,
          pointerEvents: "auto",
        },
        "@media (hover: hover)": {
          "&:hover .alert-row-meta, &:focus-within .alert-row-meta": {
            visibility: "hidden",
          },
        },
      }}
    >
      {selectable && (
        <Checkbox
          size="small"
          checked={selected}
          onClick={(event) => event.stopPropagation()}
          onChange={stop(onToggleSelect)}
          inputProps={{ "aria-label": `Select ${alert.title}` }}
          sx={{
            p: 0.5,
            mt: 0.25,
            color: colors.muted,
            "&.Mui-checked": { color: colors.accent },
          }}
        />
      )}

      <Box
        sx={{
          width: compact ? 34 : 38,
          height: compact ? 34 : 38,
          flexShrink: 0,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isResolved ? resolvedTone.fg : tone.fg,
          bgcolor: isResolved ? resolvedTone.bg : tone.bg,
          border: `1px solid ${isResolved ? resolvedTone.border : tone.border}`,
        }}
      >
        {isResolved ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />
        ) : (
          <SectionIcon
            icon={alertsStore.sectionIcon(alert.section)}
            section={alert.section}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <CustomTypography
            variant="body2"
            color={colors.title}
            fontWeight={isUnread ? 700 : 500}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: compact ? "0.82rem" : "0.875rem",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              letterSpacing: "-0.005em",
            }}
          >
            {alert.title}
          </CustomTypography>
          <Box
            className="alert-row-meta"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              pt: 0.25,
            }}
          >
            <Tooltip title={formatAlertDateTime(alert.last_occurred_at)}>
              <Box component="span">
                <CustomTypography
                  variant="caption"
                  color={isUnread ? colors.accent : colors.muted}
                  fontWeight={isUnread ? 600 : 500}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {formatAlertRelative(alert.last_occurred_at)}
                </CustomTypography>
              </Box>
            </Tooltip>
            {isUnread && (
              <Box
                aria-label="Unread"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: colors.accent,
                  boxShadow: `0 0 0 3px ${colors.unreadBg}`,
                }}
              />
            )}
          </Box>
        </Box>

        {alert.body && (
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{
              mt: 0.4,
              display: "-webkit-box",
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {alert.body}
          </CustomTypography>
        )}

        <Box
          sx={{
            mt: 0.9,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          <AlertPill
            label={alert.ref.display}
            tone={{
              fg: colors.text,
              bg: colors.surfaceMuted,
              border: colors.border,
            }}
            mono
          />
          {!compact && (
            <AlertPill label={alertsStore.sectionLabel(alert.section)} />
          )}
          {alert.occurrence_count > 1 && (
            <Tooltip title={`Occurred ${alert.occurrence_count} times`}>
              <span>
                <AlertPill label={`×${alert.occurrence_count}`} tone={tone} />
              </span>
            </Tooltip>
          )}
          {isResolved && (
            <AlertPill
              label={
                alert.resolution?.by?.name
                  ? `Resolved by ${alert.resolution.by.name}`
                  : "Resolved"
              }
              tone={resolvedTone}
            />
          )}
          {alert.status === "OPEN" && alert.is_resolvable && !compact && (
            <AlertPill
              label="Open"
              tone={{ fg: tone.fg, bg: "transparent", border: tone.border }}
            />
          )}
          {isArchived && <AlertPill label="Archived" />}
        </Box>
      </Box>

      <Box
        className="alert-row-actions"
        sx={{
          position: "absolute",
          right: compact ? 12 : 16,
          top: compact ? 10 : 12,
          display: "flex",
          gap: 0.5,
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.15s ease",
          bgcolor: "transparent",
          "@media (hover: none)": { display: "none" },
        }}
      >
        {isUnread && onMarkRead && (
          <Tooltip title="Mark as read">
            <IconButton size="small" sx={actionSx} onClick={stop(onMarkRead)}>
              <DoneOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {!isArchived && onArchive && (
          <Tooltip title="Archive">
            <IconButton size="small" sx={actionSx} onClick={stop(onArchive)}>
              <ArchiveOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {isArchived && onUnarchive && (
          <Tooltip title="Move to inbox">
            <IconButton size="small" sx={actionSx} onClick={stop(onUnarchive)}>
              <UnarchiveOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="View alert details">
          <IconButton size="small" sx={actionSx} onClick={stop(onOpenDetail)}>
            <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default observer(AlertRow);
